import { lineIdOf } from "./variantCore";
/**
 * branchAvailability: what a product is at ONE branch, in shopper words.
 * ---------------------------------------------------------------------------
 * Branch operations (4 Oct 2026): a brand, restaurant or pharmacy branch runs
 * its own on/off switch, sold out and price. Every storefront read named with
 * `?outletId=` returns each product with
 *
 *   branch: { location, orderable, reason, soldOutUntil, priceDiffers }
 *   notAt:  ["main" | outletId, ...]   (omitted when empty)
 *
 * and this file is the one place on the website that turns those two fields,
 * and the cart check the server answers with (POST /api/catalog/branch-quote),
 * into the badges, the "Get it from" choice and the review dialog lines.
 * Pages never spell the copy themselves, so every vertical says the same
 * thing.
 *
 * A product without `branch` comes from a server that predates branches (or a
 * read that named no location): it reads as orderable, exactly as before.
 *
 * The same logic, names and copy as the mobile apps
 * (Lusuku_Expo_Clean app/utility/branchAvailability.js), so a shopper reads
 * the same words on the website and on the phone. Pure functions, no React.
 * ---------------------------------------------------------------------------
 */
import { sortLocationsForPicker } from "./vendorOutlets";

/** The vendor's own main location, as the server writes it. */
export const MAIN = "main";

export const REASONS = Object.freeze({
  SOLD_OUT: "SOLD_OUT",
  SWITCHED_OFF: "SWITCHED_OFF",
  NOT_SOLD_HERE: "NOT_SOLD_HERE",
});

/** LineVerdict statuses from the cart check and the order-create 409. */
export const LINE_STATUS = Object.freeze({
  OK: "OK",
  SOLD_OUT_HERE: "SOLD_OUT_HERE",
  SWITCHED_OFF: "SWITCHED_OFF",
  NOT_SOLD_HERE: "NOT_SOLD_HERE",
  PRICE_CHANGED: "PRICE_CHANGED",
  NOT_FOUND: "NOT_FOUND",
});

/** The order-create refusal that opens the review dialog. */
export const BRANCH_LINES_CHANGED = "BRANCH_LINES_CHANGED";

const isRecord = (value) => Boolean(value) && typeof value === "object";
const finite = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

/**
 * The server's key for a location: "main" or the outlet id. Takes a location
 * card from vendorOutlets.vendorLocationsOf (its own row is the main branch),
 * a bare outlet id, or nothing (main).
 */
export const locationKeyOf = (location) => {
  if (isRecord(location)) {
    if (location.isOutlet && location._id) return String(location._id);
    if (location.isOutlet === false || location.isMainBranch) return MAIN;
    return location._id ? String(location._id) : MAIN;
  }
  if (location === null || location === undefined || location === "") return MAIN;
  const key = String(location).trim();
  return !key || key.toLowerCase() === MAIN ? MAIN : key;
};

/** The outlet id for a location key: null for the main location. */
export const outletIdOfKey = (key) => {
  const where = locationKeyOf(key);
  return where === MAIN ? null : where;
};

/** A cart line's product id, whatever shape the vertical stores it in. */
export const lineProductIdOf = (line) =>
  String(line?.productId || line?._id || line?.product || "");

/**
 * One product's state at the location it was read for.
 * `known` is false when the read carried no branch block (older server, or a
 * read with no location): the product then reads as orderable.
 */
export const branchStateOf = (product) => {
  const notAt = Array.isArray(product?.notAt) ? product.notAt.map(String) : [];
  const branch = product?.branch;
  if (!isRecord(branch)) {
    return {
      known: false,
      location: null,
      orderable: true,
      reason: null,
      soldOutUntil: null,
      priceDiffers: false,
      notAt,
    };
  }
  return {
    known: true,
    location: branch.location ? String(branch.location) : MAIN,
    orderable: branch.orderable !== false,
    reason: branch.reason || null,
    soldOutUntil: branch.soldOutUntil || null,
    priceDiffers: branch.priceDiffers === true,
    notAt,
  };
};

/** True when the product cannot be ordered at the location it was read for. */
export const blockedHere = (product) => !branchStateOf(product).orderable;

const stateFrom = (state) =>
  isRecord(state) && "orderable" in state && "notAt" in state ? state : branchStateOf(state);

/**
 * The badge for a product that cannot be ordered here, or null.
 *   "Sold out today at {branch}" | "Sold out at {branch}" |
 *   "Not available at {branch}"  | "Not sold at {branch}"
 * `branchName` is the location's name (the main location uses the vendor
 * name, LocationPicker.locationName).
 */
export const soldOutLabel = (state, branchName) => {
  const s = stateFrom(state);
  if (!s || s.orderable) return null;
  const at = branchName ? ` at ${branchName}` : "";
  if (s.reason === REASONS.SOLD_OUT) {
    return s.soldOutUntil === "UNTIL_BACK" ? `Sold out${at}` : `Sold out today${at}`;
  }
  if (s.reason === REASONS.NOT_SOLD_HERE) return `Not sold${at}`;
  return `Not available${at}`;
};

/** What the Add button says when the product cannot be ordered here, or null. */
export const addButtonLabel = (state) => {
  const s = stateFrom(state);
  if (!s || s.orderable) return null;
  return s.reason === REASONS.SOLD_OUT ? "Sold out" : "Unavailable";
};

/**
 * The best other location that has this product right now: the first of
 * sortLocationsForPicker (open locations that deliver to the shopper rank
 * first, then the nearest) whose key is not in `notAt` and is not the one
 * the product was read at. Null when the product is orderable here or no
 * other location has it.
 *
 * @param {object}   product
 * @param {object[]} locations  vendorOutlets.vendorLocationsOf(vendor, ...)
 * @param {object}   [options]  { isOpen, reaches }, as for the picker
 */
export const nearestLocationWith = (product, locations, { isOpen, reaches } = {}) => {
  const state = branchStateOf(product);
  if (state.orderable) return null;
  const blocked = new Set(state.notAt);
  return (
    sortLocationsForPicker(locations, { isOpen, reaches }).find((location) => {
      const key = locationKeyOf(location);
      return key !== state.location && !blocked.has(key);
    }) || null
  );
};

/**
 * What to offer for a product that cannot be ordered here:
 *   { kind: "nearest", location }  another location has it
 *   { kind: "none" }               none of the vendor's locations has it
 *   null                           orderable here, or a one-location vendor
 */
export const branchAlternative = (product, locations, options = {}) => {
  if (!blockedHere(product)) return null;
  const list = Array.isArray(locations) ? locations.filter(isRecord) : [];
  if (list.length < 2) return null;
  const location = nearestLocationWith(product, list, options);
  return location ? { kind: "nearest", location } : { kind: "none" };
};

/** "Get it from Acacia Mall" */
export const getItFromLabel = (name) => `Get it from ${name || "another branch"}`;

/** "1.2 km away", or null when the distance is unknown. */
export const distanceAwayLabel = (location) =>
  location?.distance ? `${location.distance} km away` : null;

/** "Not available at any Yalelo branch right now" */
export const notAnywhereLabel = (vendorName) =>
  vendorName
    ? `Not available at any ${vendorName} branch right now`
    : "Not available at any branch right now";

/** The dialog before a cart moves to another location. */
export const moveCartCopy = ({ to, from } = {}) => {
  const target = to || "this branch";
  return {
    title: `Move your cart to ${target}?`,
    message: `We will check each item at ${target}. Anything it cannot supply, or that costs a different amount there, is shown before you pay.`,
    moveLabel: "Move cart",
    stayLabel: `Stay at ${from || "this branch"}`,
  };
};

/** The review dialog's title. */
export const reviewTitle = (branchName) => `Changes at ${branchName || "this branch"}`;

/**
 * What the shopper still has to do in the review dialog while Continue is
 * disabled, or null once every line is answered. A greyed-out button with
 * nothing beside it reads as broken. Same words as the apps.
 */
export const reviewHint = (items) => {
  const open = (Array.isArray(items) ? items : []).filter(
    (item) => isRecord(item) && !item.resolved
  );
  if (!open.length) return null;
  return open.some((item) => item.canKeep)
    ? "Choose Keep at new price or Remove on each item to continue."
    : "Remove these items to continue.";
};

/**
 * Why a product cannot be ordered here, as one sentence for a dialog, or null
 * when it can. Sold out for today comes back tomorrow on its own; the other
 * reasons hold until the branch changes them. Same words as the apps.
 */
export const blockedMessage = (state, itemName) => {
  const s = stateFrom(state);
  if (!s || s.orderable) return null;
  const name = itemName || "This item";
  if (s.reason === REASONS.SOLD_OUT) {
    return s.soldOutUntil === "UNTIL_BACK"
      ? `${name} is sold out here for now.`
      : `${name} has sold out for today. It will be back tomorrow.`;
  }
  return `${name} cannot be ordered here right now.`;
};

/**
 * The notice when the branch on screen stops taking orders (switched off
 * while the shopper is on it, or named by an old link) and the storefront
 * moves to the best branch left. Same words on every storefront and in the
 * apps; the website shows `${title}. ${message}` in one toast.
 */
export const branchClosedNotice = ({ closed, next, cartMoved = false } = {}) => ({
  title: `${closed || "This branch"} is not taking orders right now`,
  message: `You are now ordering from ${next || "another branch"}.${
    cartMoved ? " Your cart moved with you." : ""
  }`,
});

const REMOVE_ONLY = Object.freeze({
  [LINE_STATUS.SOLD_OUT_HERE]: (at) => `Sold out at ${at}`,
  [LINE_STATUS.SWITCHED_OFF]: (at) => `Not available at ${at}`,
  [LINE_STATUS.NOT_SOLD_HERE]: (at) => `Not sold at ${at}`,
});

/**
 * The review dialog's lines: every cart line the server flagged, with the
 * words and the actions it offers. NOT_FOUND is left out, as the server gate
 * leaves it out: that line is its vertical validator's to refuse.
 *
 * @param {object[]|object} lines  the cart lines (any vertical; a cart map works too)
 * @param {object[]} verdicts      LineVerdicts from the quote or the 409
 * @param {object}   options       { branchName, formatMoney }
 * @returns {Array<{ productId, name, status, label, canKeep, newPrice, oldPrice, lineTotal }>}
 */
export const diffCartAgainst = (lines, verdicts, { branchName, formatMoney } = {}) => {
  const at = branchName || "this branch";
  const money = typeof formatMoney === "function" ? formatMoney : (n) => String(n);
  const byId = new Map(
    (Array.isArray(lines) ? lines : Object.values(lines || {})).filter(isRecord).map((line) => [lineIdOf(line), line])
  );
  const out = [];
  for (const verdict of Array.isArray(verdicts) ? verdicts : []) {
    if (!isRecord(verdict)) continue;
    const lookupId = String(verdict.lineId || verdict.productId || "");
    const line = byId.get(lookupId) || (Array.isArray(lines) ? lines : Object.values(lines || {})).find((l) => lineProductIdOf(l) === String(verdict.productId));
    if (!line) continue;
    const productId = lineIdOf(line);
    const status = verdict.status;
    if (status === LINE_STATUS.OK || status === LINE_STATUS.NOT_FOUND || !status) continue;
    const choices = Object.values(line.selectedSpecs || {}).filter(Boolean).join(", ");
    const name = (line.name || verdict.name || "") + (choices ? ` (${choices})` : "");
    const lineTotal = finite(verdict.lineTotal) ? Number(verdict.lineTotal) : null;
    if (status === LINE_STATUS.PRICE_CHANGED && finite(verdict.unitPrice)) {
      const newPrice = Number(verdict.unitPrice) + (Number(verdict.specPriceDelta ?? line.specPriceDelta) || 0);
      const previousBase = finite(verdict.previousPrice)
        ? Number(verdict.previousPrice)
        : finite(line.price)
          ? Number(line.price)
          : null;
      const oldPrice = previousBase === null ? null : previousBase + (Number(line.specPriceDelta) || 0);
      out.push({
        productId,
        name,
        status,
        label:
          oldPrice === null
            ? `Price at ${at}: ${money(newPrice)}.`
            : `Price at ${at}: ${money(newPrice)}. Was ${money(oldPrice)}.`,
        canKeep: true,
        newPrice,
        oldPrice,
        lineTotal,
      });
      continue;
    }
    const label = (REMOVE_ONLY[status] || REMOVE_ONLY[LINE_STATUS.SWITCHED_OFF])(at);
    out.push({ productId, name, status, label, canKeep: false, newPrice: null, oldPrice: null, lineTotal });
  }
  return out;
};

/**
 * One live `branchProduct:changed` applied to one storefront product. Returns
 * the product untouched when the event is about another product.
 *
 * `notAt` follows every location's change, so "Get it from" stays right; the
 * branch block changes only when the event is for the location the product
 * was read at. A product switched off at head office stays off: the event
 * judges the branch row alone, and head office wins everywhere.
 */
export const applyBranchEventToProduct = (product, payload) => {
  if (!isRecord(product) || !isRecord(payload)) return product;
  if (String(product._id) !== String(payload.productId || "")) return product;
  if (product.enabled === false) return product;
  const where = locationKeyOf(payload.location);
  const orderable = payload.orderable !== false;
  const notAt = new Set(Array.isArray(product.notAt) ? product.notAt.map(String) : []);
  if (orderable) notAt.delete(where);
  else notAt.add(where);
  const next = { ...product };
  if (notAt.size) next.notAt = [...notAt];
  else delete next.notAt;
  if (isRecord(product.branch) && locationKeyOf(product.branch.location) === where) {
    next.branch = {
      ...product.branch,
      orderable,
      reason: orderable ? null : payload.reason || REASONS.SWITCHED_OFF,
      soldOutUntil: orderable ? null : payload.soldOutUntil || null,
    };
  }
  return next;
};

/**
 * The live event applied to every product of a section tree, in place (for
 * an Immer draft in a storefront slice). True when a product changed.
 */
export const applyBranchEventToSections = (sections, payload) => {
  if (!Array.isArray(sections) || !isRecord(payload)) return false;
  let changed = false;
  for (const section of sections) {
    const products = Array.isArray(section?.products) ? section.products : null;
    if (!products) continue;
    for (let i = 0; i < products.length; i += 1) {
      const next = applyBranchEventToProduct(products[i], payload);
      if (next !== products[i]) {
        products[i] = next;
        changed = true;
      }
    }
  }
  return changed;
};

/**
 * Is this live event about the vendor on screen? The payload names the
 * vendor by its document id (`parentId`) and its owner's id
 * (`vendorUserId`); a storefront URL may carry either.
 */
export const branchEventIsFor = (payload, ids) => {
  if (!isRecord(payload)) return false;
  const mine = new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean).map(String));
  return (
    mine.has(String(payload.parentId || "")) || mine.has(String(payload.vendorUserId || ""))
  );
};

/**
 * The order-create 409 BRANCH_LINES_CHANGED, read off a response body (or a
 * rejected thunk's payload that kept it). Null for any other answer.
 */
export const branchRefusalOf = (body) =>
  isRecord(body) && body.code === BRANCH_LINES_CHANGED && Array.isArray(body.lines)
    ? { code: BRANCH_LINES_CHANGED, location: isRecord(body.location) ? body.location : null, lines: body.lines }
    : null;

/**
 * The fields a create-order thunk keeps on its rejected payload so the
 * checkout can open the review dialog from them: `{ lines, location }` for a
 * BRANCH_LINES_CHANGED body, nothing otherwise.
 */
export const branchRefusalFields = (body) => {
  const refusal = branchRefusalOf(body);
  return refusal ? { lines: refusal.lines, location: refusal.location } : {};
};
